"use client"
import React, { useState, useEffect, useMemo } from "react";
import useApi from "@/components/api/useApi";
import LocatorContainer from "@/components/locator/LocatorContainer";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import PageContainer from "@/components/shared/PageContainer";
import LocalStorageAlert from "@/components/shared/LocalStorageAlert";
import RELocatorContainer from "@/components/locator/re/RELocatorContainer";
import useSystemLayers from "@/components/shared/map/useSystemLayers";
import { useTenant } from "@/lib/providers/TenantProvider";
import { DEFAULT_PERSPECTIVE } from "@/components/shared/map/mapPerspective";

import "@/components/locator/EmbeddedLocatorWidget.css";

// the requested mode when the community has the add-on it needs, else business
const resolveSearchMode = (mode, community) => {
  if (mode === "service" && community.addOns.includes("SERVICE_PROFILE")) {
    return "service";
  } else if (mode === "vendor" && community.addOns.includes("MARKETPLACE")) {
    return "vendor";
  } else if (mode === "re" && community.addOns.includes("PAU")) {
    return "re";
  }
  return "business";
};

const decodeLocalStorageAlert = lsa => {
  try {
    const b = new Buffer(lsa, "base64");
    return JSON.parse(b.toString());
  } catch (error) {
    console.error("Error decoding LSA parameter:", error);
    return null;
  }
};

/**
 * Settings come from a saved LocatorConfiguration (?cfg=<id>) when given; the legacy query parameters (mode, bg,
 * lsa, c, openNow) still work on their own and override the configuration when both are present.
 */
const resolveSettings = (searchParams, config, configId, community) => {
  const param = name => (searchParams.has(name) ? searchParams.get(name) : null);

  const searchMode = resolveSearchMode(param("mode") ?? config?.mode, community);
  const isBusiness = searchMode === "business";

  let introMessage = null;
  if (param("lsa")) {
    introMessage = decodeLocalStorageAlert(param("lsa"));
  } else if (config?.introMessage?.enabled) {
    introMessage = {
      ls: `locator-${configId}-intro-${config.introMessage.revision ?? 0}`,
      type: config.introMessage.variant ?? "info",
      heading: config.introMessage.heading,
      text: config.introMessage.text
    };
  }

  return {
    searchMode,
    category: param("c") ?? config?.defaultCategory ?? null,
    // openNow=false hides the "Open Now" filter toggle (business mode)
    showOpenNow:
      param("openNow") != null
        ? param("openNow") !== "false"
        : config?.showOpenNow ?? true,
    featureGroupId: param("bg") ?? config?.backgroundFeatureGroupId ?? null,
    introMessage,
    showBoundary: !!config?.showBoundary,
    showBuildingFootprints: isBusiness && !!config?.showBuildingFootprints,
    buildingColor: config?.buildingColor ?? null,
    showRealEstateLayer:
      isBusiness && community.addOns.includes("PAU") && !!config?.showRealEstateLayer,
    allowFullscreen: !!config?.allowFullscreen,
    perspective: config?.perspectiveView ? DEFAULT_PERSPECTIVE : null,
    pinLayerIds: config?.pinLayerIds ?? [],
    brandBadge: config?.brandBadge?.enabled ? config.brandBadge : null,
    eventEntries: config?.eventLayer?.enabled ? config.eventEntries ?? [] : []
  };
};

const EmbeddedLocatorWidget = props => {
  const { profile, community } = useTenant();
  const router = useRouter();
  const searchParams = useSearchParams();
  const key = searchParams.get("key");
  const configId = searchParams.get("cfg");

  const api = useApi();
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [backgroundGeoJson, setBackgroundGeoJson] = useState(null);
  const [pinLayers, setPinLayers] = useState(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (profile && community && !loaded) {
      if (configId) {
        api
          .getPublicLocatorConfiguration(profile.resourceName, configId, key)
          .then(config => {
            setSettings(resolveSettings(searchParams, config, configId, community));
            setLoaded(true);
          })
          .catch(error => {
            console.error("Unable to load locator configuration", error);
            setError(error);
            setLoaded(true);
          });
      } else {
        setSettings(resolveSettings(searchParams, null, null, community));
        setLoaded(true);
      }
    }
  }, [profile, community, loaded]);

  useEffect(() => {
    if (profile && settings?.featureGroupId) {
      api
        .getFeatureGroupGeoJson(profile.resourceName, settings.featureGroupId)
        .then(geojson => {
          setBackgroundGeoJson(geojson);
        });
    }
  }, [profile, settings?.featureGroupId]);

  useEffect(() => {
    if (profile && settings?.pinLayerIds?.length > 0) {
      api
        .getPublicPinLayers(profile.resourceName, settings.pinLayerIds)
        .then(layers => {
          setPinLayers(layers);
        })
        .catch(error => {
          console.log("Unable to load pin layers", error);
        });
    }
  }, [profile, settings?.pinLayerIds]);

  const systemLayerTypes = useMemo(() => {
    const types = [];
    if (settings?.showBoundary) types.push("BOUNDARY");
    if (settings?.showBuildingFootprints) types.push("BUILDING_FOOTPRINTS");
    return types;
  }, [settings?.showBoundary, settings?.showBuildingFootprints]);

  const loadedSystemLayers = useSystemLayers(
    profile?.resourceName,
    systemLayerTypes,
    key
  );

  const systemLayers = useMemo(
    () =>
      loadedSystemLayers.map(layer =>
        layer.type === "BUILDING_FOOTPRINTS" && settings?.buildingColor
          ? { ...layer, color: settings.buildingColor }
          : layer
      ),
    [loadedSystemLayers, settings?.buildingColor]
  );

  useEffect(() => {
    if (loaded && error) router.replace("/403");
  }, [loaded, error, router]);

  if (loaded) {
    if (error || !settings) {
      return null;
    } else
      return (
        <PageContainer
          name={profile.resourceName}
          profile={profile}
          showHeader={false}
          noFooter={true}
          branded={true}
        >
          <div
            className={
              "fullscreen-map" +
              (settings.brandBadge ? " with-brand-badge" : "")
            }
          >
            {settings.introMessage && (
              <LocalStorageAlert {...settings.introMessage} />
            )}
            {settings.searchMode === "re" ? (
              <RELocatorContainer
                profile={profile}
                community={community}
                token={key}
                backgroundGeoJson={backgroundGeoJson}
                systemLayers={systemLayers}
                pinLayers={pinLayers}
                eventEntries={settings.eventEntries}
                allowFullscreen={settings.allowFullscreen}
                brandBadge={settings.brandBadge}
                perspective={settings.perspective}
              />
            ) : (
              <LocatorContainer
                profile={profile}
                community={community}
                searchMode={settings.searchMode}
                token={key}
                backgroundGeoJson={backgroundGeoJson}
                category={settings.category}
                showOpenNow={settings.showOpenNow}
                systemLayers={systemLayers}
                pinLayers={pinLayers}
                eventEntries={settings.eventEntries}
                allowFullscreen={settings.allowFullscreen}
                brandBadge={settings.brandBadge}
                enableBuildingSelection={settings.showBuildingFootprints}
                showRealEstateLayer={settings.showRealEstateLayer}
                perspective={settings.perspective}
              />
            )}
          </div>
        </PageContainer>
      );
  } else {
    return <></>;
  }
};

export default EmbeddedLocatorWidget;
